import type { LearnLesson } from '../../lib/api/types.js';
import type { ContentListItemState } from '../../ui/components/ContentListItem.tsx';

/**
 * Turning the learner's content read into the three states the design system
 * defines — and only those three.
 *
 * There is no `locked` state here and there must not be one: the design
 * system defines active / available / completed and nothing more, and
 * progression gating is deferred by the specification. A learner may open any
 * Lesson. What the platform tracks is where they *are*, not where they are
 * allowed to be.
 *
 * `completed` is the server's derivation (FR-057), never recomputed here from
 * a stored flag — these helpers only count what the response already carries,
 * so a Lesson an administrator has just extended reads as reopened on the very
 * next load without this file knowing anything about it.
 */

export function exerciseCount(lesson: LearnLesson): number {
  return lesson.exercises.length;
}

export function countCompleted(lesson: LearnLesson): number {
  return lesson.exercises.filter((exercise) => exercise.completed).length;
}

/**
 * `active` is the Lesson the learner's session currently points into — one at
 * most, and only while it is unfinished. A completed Lesson is `completed`
 * even when the cursor still sits in it, because "you finished this" is the
 * more useful of the two things to say.
 */
export function lessonState(lesson: LearnLesson, currentLessonId: string | null): ContentListItemState {
  if (lesson.completed) return 'completed';
  if (lesson.id === currentLessonId) return 'active';
  return 'available';
}
